
import React, { useState, useEffect } from 'react';
import LoggingService, { LogEntry, LogLevel } from '@/services/LoggingService';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Search, Filter, RefreshCw, ChevronDown, ChevronUp, FileText } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';

const LogViewer: React.FC = () => {
  const [logs, setLogs] = useState<LogEntry[]>([]);
  const [totalLogs, setTotalLogs] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  
  // Pagination state
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(50);
  
  // Filter state
  const [levelFilter, setLevelFilter] = useState<LogLevel | 'all'>('all');
  const [sourceFilter, setSourceFilter] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  // Selected log for dialog
  const [selectedLog, setSelectedLog] = useState<LogEntry | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  const fetchLogs = async () => {
    setIsLoading(true);
    try {
      const { logs: fetchedLogs, count } = await LoggingService.getLogs({
        page,
        limit,
        level: levelFilter !== 'all' ? levelFilter as LogLevel : undefined,
        source: sourceFilter || undefined,
        search: searchQuery || undefined,
      });
      
      setLogs(fetchedLogs);
      setTotalLogs(count);
    } catch (error) {
      console.error('Failed to fetch logs:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Initial load and when filters/pagination change
  useEffect(() => {
    fetchLogs();
  }, [page, limit, levelFilter, sourceFilter, searchQuery]);

  const handleRefresh = () => {
    fetchLogs();
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setPage(1); // Reset to first page when searching
    fetchLogs();
  };

  const getLevelColor = (level: LogLevel): string => {
    switch (level) {
      case 'debug': return 'bg-slate-500';
      case 'info': return 'bg-blue-500';
      case 'warn': return 'bg-amber-500';
      case 'error': return 'bg-red-500';
      case 'fatal': return 'bg-purple-700';
      default: return 'bg-gray-500';
    }
  };

  const formatTimestamp = (timestamp: string) => {
    try {
      return formatDistanceToNow(new Date(timestamp), { addSuffix: true });
    } catch (e) {
      return 'Invalid date';
    }
  };
  
  const openLogDetailsDialog = (log: LogEntry) => {
    setSelectedLog(log);
    setDialogOpen(true);
  };

  return (
    <Card className="w-full">
      <CardHeader className="pb-3">
        <CardTitle className="text-xl font-semibold">System Logs</CardTitle>
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mt-4">
          <form onSubmit={handleSearch} className="flex-1 flex gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                type="text"
                placeholder="Search logs..."
                className="pl-8"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
            <Button type="submit" variant="outline" size="icon">
              <Search className="h-4 w-4" />
            </Button>
          </form>
          
          <div className="flex gap-2">
            <Select
              value={levelFilter}
              onValueChange={(value) => {
                setLevelFilter(value as LogLevel | 'all');
                setPage(1);
              }}
            >
              <SelectTrigger className="w-[130px]">
                <SelectValue placeholder="Log Level" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Levels</SelectItem>
                <SelectItem value="debug">Debug</SelectItem>
                <SelectItem value="info">Info</SelectItem>
                <SelectItem value="warn">Warning</SelectItem>
                <SelectItem value="error">Error</SelectItem>
                <SelectItem value="fatal">Fatal</SelectItem>
              </SelectContent>
            </Select>
            
            <Button 
              variant="outline" 
              size="icon" 
              onClick={handleRefresh}
              disabled={isLoading}
            >
              <RefreshCw className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        </div>
      </CardHeader>
      
      <CardContent>
        <div className="rounded-md border">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-muted/50">
                  <th className="py-2 px-4 text-left font-medium">Level</th>
                  <th className="py-2 px-4 text-left font-medium">Message</th>
                  <th className="py-2 px-4 text-left font-medium">Source</th>
                  <th className="py-2 px-4 text-left font-medium">Time</th>
                  <th className="py-2 px-4 text-center font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {logs.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="text-center py-4 text-muted-foreground">
                      {isLoading ? 'Loading logs...' : 'No logs found'}
                    </td>
                  </tr>
                ) : (
                  logs.map((log) => (
                    <tr key={log.id || `log-${logs.indexOf(log)}`} 
                        className="border-t hover:bg-muted/50">
                      <td className="py-2 px-4">
                        <Badge className={getLevelColor(log.level)}>
                          {log.level.toUpperCase()}
                        </Badge>
                      </td>
                      <td className="py-2 px-4 max-w-md truncate">{log.message}</td>
                      <td className="py-2 px-4">{log.source || 'N/A'}</td>
                      <td className="py-2 px-4 whitespace-nowrap">
                        {log.timestamp ? formatTimestamp(log.timestamp) : 'N/A'}
                      </td>
                      <td className="py-2 px-4 text-center">
                        <Button 
                          variant="ghost" 
                          size="icon"
                          onClick={() => openLogDetailsDialog(log)}
                        >
                          <FileText className="h-4 w-4" />
                        </Button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
        
        {logs.length > 0 && (
          <div className="flex justify-between items-center mt-4 text-sm text-muted-foreground">
            <div>
              Showing {(page - 1) * limit + 1} to {Math.min(page * limit, totalLogs)} of {totalLogs} logs
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1 || isLoading}
              >
                Previous
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(p => p + 1)}
                disabled={(page * limit) >= totalLogs || isLoading}
              >
                Next
              </Button>
            </div>
          </div>
        )}
      </CardContent>

      {/* Log Details Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Log Details</DialogTitle>
            <DialogDescription>
              {selectedLog?.timestamp && (
                <span className="text-sm">
                  {new Date(selectedLog.timestamp).toLocaleString()}
                </span>
              )}
            </DialogDescription>
          </DialogHeader>
          
          {selectedLog && (
            <div className="space-y-4 mt-2">
              <div>
                <h4 className="font-semibold mb-1">Level:</h4>
                <Badge className={getLevelColor(selectedLog.level)}>
                  {selectedLog.level.toUpperCase()}
                </Badge>
              </div>
              
              <div>
                <h4 className="font-semibold mb-1">Source:</h4>
                <p>{selectedLog.source || 'N/A'}</p>
              </div>
              
              <div>
                <h4 className="font-semibold mb-1">Message:</h4>
                <p className="break-words">{selectedLog.message}</p>
              </div>
              
              <div>
                <h4 className="font-semibold mb-1">Details:</h4>
                {selectedLog.details ? (
                  <pre className="bg-muted p-3 rounded-md overflow-x-auto whitespace-pre-wrap max-h-[300px] overflow-y-auto">
                    {typeof selectedLog.details === 'object' 
                      ? JSON.stringify(selectedLog.details, null, 2) 
                      : String(selectedLog.details)}
                  </pre>
                ) : (
                  <p className="text-muted-foreground italic">No additional details available</p>
                )}
              </div>
              
              {selectedLog.user_id && (
                <div>
                  <h4 className="font-semibold mb-1">User ID:</h4>
                  <p>{selectedLog.user_id}</p>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default LogViewer;
